import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  Download
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminUser {
  id: number
  name: string
  email: string
  rpps: string
  specialty: string
  role: string
  community_name: string | null
  total_points: number
  level: number
  created_at: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  role: string | null
  specialty: string | null
  community_id: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface IndexProps {
  users: AdminUser[]
  pagination: Pagination
  filters: Filters
  specialties: string[]
  communities: Community[]
  roles: string[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getRoleBadgeColor(role: string): string {
  switch (role) {
    case 'admin': return 'bg-yellow-100 text-yellow-800'
    case 'moderator': return 'bg-green-100 text-green-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

function getRoleLabel(role: string): string {
  switch (role) {
    case 'admin': return 'Admin'
    case 'moderator': return 'Modérateur'
    default: return 'Membre'
  }
}

export default function Index({ users, pagination, filters, specialties, communities, roles }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/users', {
      ...filters,
      ...newFilters,
      page: 1 // Reset to first page on filter change
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/users', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((user: AdminUser) => {
    if (confirm(`Voulez-vous vraiment supprimer ${user.name} ?`)) {
      router.delete(`/admin/users/${user.id}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Utilisateurs"
      breadcrumbs={[{ label: 'Utilisateurs' }]}
    >
      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par pseudo, nom, prénom, email, RPPS ou SIRET..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={`/admin/users/export_csv?${new URLSearchParams(
              Object.entries(filters).filter(([, v]) => v != null) as [string, string][]
            ).toString()}`}
          >
            <Button variant="secondary" className="flex items-center gap-2">
              <Download size={18} />
              Export CSV
            </Button>
          </a>
          <Link href="/admin/users/new">
            <Button className="flex items-center gap-2">
              <Plus size={18} />
              Nouvel utilisateur
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-40">
            <Select
              value={filters.role || ''}
              onChange={(e) => applyFilters({ role: e.target.value || null })}
            >
              <option value="">Tous les rôles</option>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {getRoleLabel(role)}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.specialty || ''}
              onChange={(e) => applyFilters({ specialty: e.target.value || null })}
            >
              <option value="">Toutes spécialités</option>
              {specialties.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {specialty}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautés</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          {(filters.search || filters.role || filters.specialty || filters.community_id) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  role: null,
                  specialty: null,
                  community_id: null
                })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} utilisateur{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Users Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('name')}
                  >
                    Nom
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Email / RPPS
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Spécialité
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Rôle
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('points')}
                  >
                    Points
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('created_at')}
                  >
                    Inscription
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    Aucun utilisateur trouvé
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-tessan-primary text-white flex items-center justify-center text-sm font-medium">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-text-dark">{user.name}</p>
                          {user.community_name && (
                            <p className="text-xs text-text-gray">{user.community_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-text-dark">{user.email}</p>
                      <p className="text-xs text-text-gray">RPPS: {user.rpps}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-dark">{user.specialty || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-text-dark">{user.total_points.toLocaleString()}</p>
                        <p className="text-xs text-text-gray">Niv. {user.level}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/admin/users/${user.id}`}>
                          <Button variant="secondary" className="p-2">
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Link href={`/admin/users/${user.id}/edit`}>
                          <Button variant="secondary" className="p-2">
                            <Edit size={16} />
                          </Button>
                        </Link>
                        <Button
                          variant="secondary"
                          className="p-2 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(user)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Précédent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
